import { useMemo, useState } from 'react';

import {
	defaultShouldDehydrateQuery,
	matchQuery,
	MutationCache,
	QueryClient,
	QueryClientProvider
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { StatusDialog } from './components/ui/dialog';
import Teleport from './components/ui/teleport';
import createAppRouter from './routes';

export default function App() {
	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						retry: 1,
						refetchOnWindowFocus: false,
						staleTime: 60 * 5 * 1_000,
						gcTime: 5 * 1_000
					},
					dehydrate: {
						shouldDehydrateQuery: (query) =>
							defaultShouldDehydrateQuery(query) || query.state.status === 'pending'
					},
					mutations: {
						retry: 1,
						retryDelay: 500
					}
				},
				mutationCache: new MutationCache({
					onSuccess: (_data, _variables, _context, mutation) => {
						queryClient.invalidateQueries(
							{
								predicate: (query) =>
									mutation.meta?.invalidates
										?.filter(Boolean)
										.some((queryKey) => matchQuery({ queryKey }, query)) ?? false
							},
							{ cancelRefetch: false }
						);
					}
				})
			})
	);

	const router = useMemo(() => createAppRouter(queryClient), [queryClient]);

	return (
		<QueryClientProvider client={queryClient}>
			<NuqsAdapter>
				<RouterProvider router={router} future={{ v7_startTransition: true }} />
				<ToastContainer
					position="top-right"
					autoClose={3000}
					hideProgressBar={false}
					newestOnTop={false}
					closeOnClick
					pauseOnFocusLoss
					draggable
					pauseOnHover
					aria-label={'toast'}
				/>
				<Teleport>
					<StatusDialog />
				</Teleport>
			</NuqsAdapter>
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	);
}
